
import { useState, useRef, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function DatePicker({ value, onChange }: { value: string; onChange: (val: string) => void }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayValue = value
    ? (() => {
        const [y, m, d] = value.split('-');
        return `${y}/${m}/${d}`;
      })()
    : '';

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const selectDate = (day: number) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${viewYear}-${m}-${d}`);
    setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isSelected = (day: number) => {
    if (!value) return false;
    const [y, m, d] = value.split('-').map(Number);
    return y === viewYear && m - 1 === viewMonth && d === day;
  };

  const isToday = (day: number) =>
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;

  return (
    <div className="relative" ref={ref}>
      <div
        className="w-full px-4 py-3 border border-[#E0D0D0] bg-white focus:border-[#C0392B] outline-none transition-colors text-sm rounded-sm cursor-pointer flex items-center justify-between"
        onClick={() => setOpen(o => !o)}
      >
        <span className={displayValue ? 'text-[#2D2D2D]' : 'text-[#AAAAAA]'}>
          {displayValue || 'yyyy/mm/dd'}
        </span>
        <i className="ri-calendar-line text-[#C0392B]"></i>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 bg-white border border-[#E0D0D0] rounded-sm shadow-lg p-4 w-72">
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth} className="w-7 h-7 flex items-center justify-center hover:bg-[#FDF8F8] rounded cursor-pointer">
              <i className="ri-arrow-left-s-line text-[#555555]"></i>
            </button>
            <span className="text-sm font-medium text-[#2D2D2D]">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} className="w-7 h-7 flex items-center justify-center hover:bg-[#FDF8F8] rounded cursor-pointer">
              <i className="ri-arrow-right-s-line text-[#555555]"></i>
            </button>
          </div>
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs text-[#AAAAAA] py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => selectDate(day)}
                  className={`text-center text-sm py-1.5 rounded cursor-pointer transition-colors
                    ${isSelected(day) ? 'bg-[#C0392B] text-white' : isToday(day) ? 'border border-[#C0392B] text-[#C0392B]' : 'hover:bg-[#FDF8F8] text-[#2D2D2D]'}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReservationPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '2',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('date', formData.date);
      formBody.append('time', formData.time);
      formBody.append('guests', formData.guests);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6hfsng57726vudt7tlg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', date: '', time: '', guests: '2', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="https://static.readdy.ai/image/356e0a32a00e968a1405b9eb797d2bd9/d53fac562167410231decf55733abfe0.jpeg"
          alt="Reservation"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center text-white">
          <p className="text-xs tracking-[0.3em] uppercase mb-3">Book Your Table</p>
          <h1 className="text-5xl md:text-6xl font-light tracking-wide">Reservation</h1>
        </div>
      </section>

      {/* Reservation Form Section */}
      <section className="py-24 bg-white flex-grow">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] text-[#C0392B] uppercase mb-3">Reserve A Table</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#2D2D2D] mb-4">Make Your Reservation</h2>
            <div className="w-12 h-0.5 bg-[#C0392B] mx-auto mb-6"></div>
            <p className="text-base text-[#666666] leading-relaxed max-w-2xl mx-auto">
              Experience authentic Chinese cuisine in a warm and inviting atmosphere. Book your table now and let us create an unforgettable dining experience for you.
            </p>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="space-y-6 bg-[#FDF8F8] p-10 md:p-14 rounded-sm border border-[#F0E8E8]"
            data-readdy-form
            id="reservation-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm text-[#555555] mb-2 tracking-wide">Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                  className="w-full px-4 py-3 border border-[#E0D0D0] bg-white focus:border-[#C0392B] focus:outline-none transition-colors text-sm rounded-sm" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm text-[#555555] mb-2 tracking-wide">Email *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                  className="w-full px-4 py-3 border border-[#E0D0D0] bg-white focus:border-[#C0392B] focus:outline-none transition-colors text-sm rounded-sm" />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm text-[#555555] mb-2 tracking-wide">Phone *</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required
                className="w-full px-4 py-3 border border-[#E0D0D0] bg-white focus:border-[#C0392B] focus:outline-none transition-colors text-sm rounded-sm" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm text-[#555555] mb-2 tracking-wide">Date *</label>
                <DatePicker value={formData.date} onChange={val => setFormData(prev => ({ ...prev, date: val }))} />
              </div>

              <div>
                <label htmlFor="time" className="block text-sm text-[#555555] mb-2 tracking-wide">Time *</label>
                <input type="time" id="time" name="time" value={formData.time} onChange={handleChange} required
                  className="w-full px-4 py-3 border border-[#E0D0D0] bg-white focus:border-[#C0392B] focus:outline-none transition-colors text-sm rounded-sm" />
              </div>

              <div>
                <label htmlFor="guests" className="block text-sm text-[#555555] mb-2 tracking-wide">Guests *</label>
                <select id="guests" name="guests" value={formData.guests} onChange={handleChange} required
                  className="w-full px-4 py-3 border border-[#E0D0D0] bg-white focus:border-[#C0392B] focus:outline-none transition-colors text-sm rounded-sm cursor-pointer">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm text-[#555555] mb-2 tracking-wide">Special Requests</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange}
                maxLength={500} rows={5} placeholder="Any dietary restrictions or special occasions?"
                className="w-full px-4 py-3 border border-[#E0D0D0] bg-white focus:border-[#C0392B] focus:outline-none transition-colors resize-none text-sm rounded-sm"></textarea>
              <p className="text-xs text-[#AAAAAA] mt-1">{formData.message.length}/500</p>
            </div>

            <button type="submit" disabled={isSubmitting}
              className="w-full md:w-auto px-12 py-4 bg-[#C0392B] text-white text-sm tracking-wider hover:bg-[#A93226] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer rounded-sm">
              {isSubmitting ? 'Submitting...' : 'Confirm Reservation'}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 text-green-800 text-sm rounded-sm">
                Thank you! Your reservation has been received. We will contact you shortly to confirm.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-800 text-sm rounded-sm">
                Submission failed. Please try again later.
              </div>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
